import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ErpInputTableV2 } from '@/components/custom/erp/input-table-v2';
import { ErpPagination } from '@/components/custom/erp/pagination';
import { ContextMenu } from './components';
import { ContextMenuBuilder } from './utils/context-menu-builder';
import { InputTableCellParams, InputTableProps } from './types';
import { inputTableClassName } from './style';
import { cn } from '@/lib/utils';
export * from './types';

export function InputTable<T = any>({
  rows = [],
  columns,
  mode = 'add',
  className,
  height,
  autoHeight = false,
  bottomOffset = 0,
  bottomSelector,
  rowHeight = 34,
  getRowHeight: customGetRowHeight,
  getRowId,
  actionButtons,
  selectedRowId,
  onRowClick,
  onCellClick,
  checkboxSelection = false,
  selectedRowIds = [],
  onRowSelectionChange,
  totalItems,
  currentPage = 0,
  pageSize = 100,
  onPageChange,
  onPageSizeChange,
  clientPagination = false,
  hideFooter,
  enableContextMenu = false,
  onCopyData,
  onRepeatForRows,
  loading = false,
  disableVirtualization = false
}: InputTableProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [computedHeight, setComputedHeight] = useState<string>();
  const [clientPaginationModel, setClientPaginationModel] = useState({
    page: currentPage,
    pageSize
  });
  const [contextMenu, setContextMenu] = useState<{
    position: { x: number; y: number } | null;
    rowData: T | null;
    columnField: string | null;
  }>({
    position: null,
    rowData: null,
    columnField: null
  });

  const updateHeight = useCallback(() => {
    if (!autoHeight || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    let extraHeight = bottomOffset;

    if (bottomSelector) {
      const bottomEl = document.querySelector(bottomSelector) as HTMLElement | null;
      extraHeight += bottomEl?.offsetHeight ?? 0;
    }

    const availableHeight = window.innerHeight - rect.top - extraHeight;
    setComputedHeight(availableHeight > 0 ? `${availableHeight}px` : undefined);
  }, [autoHeight, bottomOffset, bottomSelector]);

  useEffect(() => {
    if (!autoHeight) return;

    updateHeight();
    window.addEventListener('resize', updateHeight);

    return () => {
      window.removeEventListener('resize', updateHeight);
    };
  }, [autoHeight, updateHeight]);

  useEffect(() => {
    if (!autoHeight || !containerRef.current) return;

    const resizeObserver = new ResizeObserver(() => updateHeight());
    resizeObserver.observe(containerRef.current);

    const raf = requestAnimationFrame(updateHeight);

    return () => {
      resizeObserver.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [autoHeight, updateHeight]);

  useEffect(() => {
    if (!clientPagination) return;

    setClientPaginationModel(previousModel => {
      if (previousModel.pageSize === pageSize) return previousModel;
      return {
        page: 0,
        pageSize
      };
    });
  }, [clientPagination, pageSize]);

  useEffect(() => {
    if (clientPagination) return;

    setClientPaginationModel({
      page: currentPage,
      pageSize
    });
  }, [clientPagination, currentPage, pageSize]);

  const resolvedHeight = height !== undefined ? (typeof height === 'number' ? `${height}px` : height) : computedHeight;
  const hasHeight = resolvedHeight !== undefined;
  const containerClassName = cn(hasHeight ? 'flex flex-col' : 'flex h-72 flex-col', className);

  const resolveRowId = useCallback(
    (row: any) => {
      const providedId = getRowId ? getRowId(row) : undefined;
      if (providedId !== undefined && providedId !== null && providedId !== '') {
        return String(providedId);
      }

      return String(row?.uuid || row?.id || row?.code || row?.key || JSON.stringify(row));
    },
    [getRowId]
  );

  const paginationModel = clientPagination ? clientPaginationModel : { page: currentPage, pageSize };
  const visibleRows = useMemo(() => {
    if (!clientPagination) return rows;

    const startIndex = paginationModel.page * paginationModel.pageSize;
    return rows.slice(startIndex, startIndex + paginationModel.pageSize);
  }, [clientPagination, paginationModel.page, paginationModel.pageSize, rows]);
  const rowCount = clientPagination ? rows.length : totalItems;
  const shouldShowPagination = Boolean(totalItems || clientPagination);
  const handlePageChange = useCallback(
    (page: number) => {
      if (clientPagination) {
        setClientPaginationModel(previousModel => ({ ...previousModel, page }));
        return;
      }

      onPageChange?.(page);
    },
    [clientPagination, onPageChange]
  );

  const getRowHeight = useCallback(
    (params: { id: string; row: any; model: any }) => {
      if (customGetRowHeight) {
        return customGetRowHeight(params);
      }

      const row = params.model || params.row;
      if (row?.ma_vt_data && row?.dvt && row?.dvt !== row?.ma_vt_data?.dvt) {
        return 55;
      }

      return rowHeight;
    },
    [customGetRowHeight, rowHeight]
  );

  const closeContextMenu = useCallback(() => {
    setContextMenu({
      position: null,
      rowData: null,
      columnField: null
    });
  }, []);

  const contextMenuOptions = useMemo(
    () =>
      ContextMenuBuilder.buildOptions({
        contextMenu,
        columns,
        getRowId,
        onCopyData,
        onRepeatForRows,
        rowsCount: rows.length
      }),
    [columns, contextMenu, getRowId, onCopyData, onRepeatForRows, rows.length]
  );

  const handleCellContextMenu = useCallback(
    (event: MouseEvent | React.MouseEvent, params: InputTableCellParams<T>) => {
      if (!enableContextMenu) return;

      event.preventDefault();
      event.stopPropagation();

      const pointerEvent = event as MouseEvent;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const menuWidth = 200;
      const menuHeight = 100;
      const adjustedX =
        pointerEvent.clientX + menuWidth > viewportWidth ? pointerEvent.clientX - menuWidth : pointerEvent.clientX;
      const adjustedY =
        pointerEvent.clientY + menuHeight > viewportHeight ? pointerEvent.clientY - menuHeight : pointerEvent.clientY;

      onCellClick?.(params);
      setContextMenu({
        position: { x: adjustedX, y: adjustedY },
        rowData: params.row,
        columnField: params.field
      });
    },
    [enableContextMenu, onCellClick]
  );

  return (
    <div
      data-excel-operation-overlay-root
      ref={containerRef}
      className={containerClassName}
      style={resolvedHeight ? { height: resolvedHeight } : undefined}
    >
      {actionButtons}

      <ErpInputTableV2
        rows={visibleRows as any[]}
        columns={columns as any}
        className='!h-auto min-h-0 flex-auto'
        tableClassName={inputTableClassName}
        selectedRowId={selectedRowId}
        getRowId={resolveRowId}
        onRowClick={onRowClick as any}
        onCellClick={onCellClick as any}
        onCellContextMenu={handleCellContextMenu as any}
        checkboxSelection={checkboxSelection}
        selectedRowIds={checkboxSelection ? selectedRowIds : selectedRowId ? [selectedRowId] : []}
        onRowSelectionChange={ids => {
          if (checkboxSelection && onRowSelectionChange) {
            onRowSelectionChange(ids);
          }
        }}
        totalItems={rowCount}
        currentPage={paginationModel.page}
        pageSize={paginationModel.pageSize}
        onPageChange={handlePageChange}
        loading={loading}
        hideHeader={false}
        showIndexColumn
        hideEmptyState
        disableColumnFilters
        indexColumnWidth={40}
        rowHeight={rowHeight}
        getRowHeight={getRowHeight as any}
        disableVirtualization={disableVirtualization}
        widthMode='content'
        scrollMode='auto'
      />

      {shouldShowPagination && !hideFooter && rowCount ? (
        <div
          className='sticky left-0 flex w-full min-w-full items-center justify-end border-t border-[#ddd] bg-white py-1.5 pr-2'
          style={{ width: 'max-content', minWidth: '100%' }}
        >
          <ErpPagination
            paginationModel={paginationModel}
            totalItems={rowCount}
            onPageChange={handlePageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </div>
      ) : null}

      {enableContextMenu && (
        <ContextMenu options={contextMenuOptions} position={contextMenu.position} onClose={closeContextMenu} />
      )}
    </div>
  );
}
